import { useCallback, useEffect } from "react";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import { Paper, Stack, TextField, Typography } from "@mui/material";
import { useUserProfile } from "@/hooks";
import { AnonymousUser } from "@/types";
import InputMask from "react-input-mask";
import isEmpty from "lodash/isEmpty";
import { CheckoutSharedProps } from "@/pages/checkout/CheckoutPage";
import pick from "lodash/pick";
import { sanitizePhone } from "@/utils/phones";
import { LocalStorage } from "@/constants/localStorage";

export const ContactFormSchema = Yup.object().shape({
  name: Yup.string().required("Campo requerido"),
  lastname: Yup.string().required("Campo requerido"),
  phone: Yup.string().min(8, "Muy Corto!").max(8, "Muy Largo!").required("Campo requerido"),
  email: Yup.string().email("Correo no válido"),
});

export type IContactForm = Partial<AnonymousUser>;

export const contactFormInitialValues: IContactForm = {
  id: "",
  name: "",
  lastname: "",
  phone: "",
  email: "",
};

export const contactFormFields = Object.keys(contactFormInitialValues);

export const onSubmitContactForm = async (values: IContactForm, sharedProps: CheckoutSharedProps) => {
  const { activeUser } = sharedProps;

  if (activeUser?.isAnonymous) {
    const lsUser = JSON.parse(localStorage.getItem(LocalStorage.USER) || "{}");
    const updatedUser = JSON.stringify({ ...lsUser, ...pick(values, contactFormFields) });
    localStorage.setItem(LocalStorage.USER, updatedUser);
  }
};

const ContactForm = () => {
  const formik = useFormikContext<IContactForm>();
  const { activeUser } = useUserProfile();

  const initializeData = useCallback(() => {
    if (!isEmpty(formik.values.name)) return;

    if (activeUser) {
      formik.setValues({
        name: activeUser?.name || "",
        lastname: activeUser?.lastname || "",
        phone: activeUser?.phone || "",
        email: activeUser?.email || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <Stack spacing={2}>
      <Typography>¿Quién recibirá el pedido?</Typography>
      <Paper>
        <Stack spacing={2} p={2}>
          <TextField
            name="name"
            label="Nombre"
            value={formik.values.name}
            error={!!formik.touched.name && !!formik.errors.name && !!formik.handleBlur}
            helperText={!!formik.touched.name && formik.errors.name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <TextField
            name="lastname"
            label="Apellido"
            value={formik.values.lastname}
            error={!!formik.touched.lastname && !!formik.errors.lastname && !!formik.handleBlur}
            helperText={!!formik.touched.lastname && formik.errors.lastname}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />

        </Stack>
      </Paper>
      <Typography>¿Cómo le contactamos?</Typography>
      <Paper>
        <Stack spacing={2} p={2}>
          <InputMask
            mask="9999-9999"
            placeholder="0000-0000"
            alwaysShowMask={false}
            value={formik.values.phone}
            onChange={(e) => {
              formik.setFieldValue("phone", sanitizePhone(e.target.value));
            }}
            onBlur={formik.handleBlur}
          >
            {() => (
              <TextField
                label="Celular"
                name="phone"
                error={!!formik.touched.phone && !!formik.errors?.phone}
                helperText={formik.touched.phone && formik.errors?.phone}
              />
            )}
          </InputMask>
          <TextField
            name="email"
            label="Correo (opcional)"
            value={formik.values.email}
            error={!!formik.errors.email}
            onChange={formik.handleChange}
            type="email"
            helperText={formik.touched.email && formik.errors?.email}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ContactForm;
